import {
  type CompiledFrameworkStructure,
  PROJECT_SEARCH_PARAM_KEY,
  parseProjectPath,
  stripEntityVersionPathSuffix,
} from "@repo/shared";
import {
  buildEntityDisplayMap,
  buildFrameworkEntityAliases,
} from "@/features/project/lib/framework-registry";
import {
  ENTITY_QUALITY_GUARD_FOCUS_SEARCH_PARAM_CLEARS,
  isEntityNavigationPath,
  isRememberedAssistantPath,
  shouldClearTransientProjectSearchParams,
  TRANSIENT_PROJECT_SEARCH_PARAM_CLEARS,
} from "@/features/project/lib/project-tab-search-params";
import type { TabMemory } from "@/routing/core/locations";
import { mergeTabSearchParams } from "@/routing/core/tabs-reducer";
import type { TabNavigationPolicy } from "@/routing/core/tabs-store";

type ProjectFrameworkStructure = Pick<CompiledFrameworkStructure, "entityDisplays"> &
  Partial<Pick<CompiledFrameworkStructure, "rootFolders" | "folderNodes">>;

function parseNavigationPath(
  path: string,
  frameworkStructure: ProjectFrameworkStructure | null | undefined
) {
  if (!frameworkStructure) {
    return null;
  }

  return parseProjectPath(path, {
    aliases: buildFrameworkEntityAliases(frameworkStructure),
    structure:
      frameworkStructure.rootFolders && frameworkStructure.folderNodes
        ? {
            rootFolders: frameworkStructure.rootFolders,
            folderNodes: frameworkStructure.folderNodes,
          }
        : null,
  });
}

function getEntityRouteTypeKey(
  path: string,
  frameworkStructure: ProjectFrameworkStructure | null | undefined
): string | null {
  const parsedPath = parseNavigationPath(path, frameworkStructure);

  return parsedPath?.routeKey === "entity" ? (parsedPath.params.entityTypeKey ?? null) : null;
}

function resolveRememberedEntityPath(
  path: string,
  frameworkStructure: ProjectFrameworkStructure | null | undefined
): string {
  const parsedPath = parseNavigationPath(path, frameworkStructure);

  return parsedPath?.routeKey === "entity" && parsedPath.baseEntityPath
    ? parsedPath.baseEntityPath
    : stripEntityVersionPathSuffix(path);
}

function resolveEntityIconKeyForPath(
  path: string,
  frameworkStructure: ProjectFrameworkStructure | null | undefined
): string | null {
  const entityTypeKey = getEntityRouteTypeKey(path, frameworkStructure);
  if (!entityTypeKey || !frameworkStructure) {
    return null;
  }

  const displays = buildEntityDisplayMap(frameworkStructure);
  return displays.get(entityTypeKey)?.iconKey ?? null;
}

function resolveCanonicalProjectPath(
  path: string,
  frameworkStructure: ProjectFrameworkStructure | null | undefined
): string {
  const parsedPath = parseNavigationPath(path, frameworkStructure);
  if (!parsedPath || parsedPath.routeKey === "notFound") {
    return path;
  }

  return parsedPath.canonicalPath !== parsedPath.normalizedPath ? parsedPath.canonicalPath : path;
}

export function createProjectNavigationPolicy(
  frameworkStructure: ProjectFrameworkStructure | null | undefined
): TabNavigationPolicy {
  return ({ path, currentSearchParams, requestedSearchParams, memory }) => {
    // LAT-990: `/ai-assistant` used to be rewritten to `memory.assistantPath` here, so the nav
    // entry silently jumped to whichever thread the tab last had. The Claudia nav row and the
    // collapsed rail's icon both open the conversations flyout now, which is where choosing a
    // conversation belongs — a nav entry that lands somewhere different every time is not
    // navigation. The memory itself is still *written* below: the contextual panel reads it to
    // know which thread it is showing (`rememberedAssistantThreadId`).
    const resolvedPath = resolveCanonicalProjectPath(path, frameworkStructure);
    const nextMemory: TabMemory = { ...memory };
    let searchParams = { ...currentSearchParams };

    if (shouldClearTransientProjectSearchParams(resolvedPath)) {
      searchParams = mergeTabSearchParams(searchParams, TRANSIENT_PROJECT_SEARCH_PARAM_CLEARS);
    }

    if (isRememberedAssistantPath(resolvedPath)) {
      // Full-screen assistant navigation owns route-derived assistant memory.
      // The Claudia side panel also writes assistantPath directly when it changes threads locally.
      nextMemory.assistantPath = resolvedPath;
    }

    if (isEntityNavigationPath(resolvedPath)) {
      const carriesQualityGuardFocus =
        requestedSearchParams?.[PROJECT_SEARCH_PARAM_KEY.qualityGuardPanelFocus] !== undefined;
      if (!carriesQualityGuardFocus) {
        searchParams = mergeTabSearchParams(
          searchParams,
          ENTITY_QUALITY_GUARD_FOCUS_SEARCH_PARAM_CLEARS
        );
      }
      const rememberedEntityPath = resolveRememberedEntityPath(resolvedPath, frameworkStructure);
      nextMemory.entityPath = rememberedEntityPath;
      const iconKey = resolveEntityIconKeyForPath(rememberedEntityPath, frameworkStructure);
      if (iconKey) {
        nextMemory.entityIconKey = iconKey;
      } else {
        delete nextMemory.entityIconKey;
      }
    }

    if (requestedSearchParams) {
      searchParams = mergeTabSearchParams(searchParams, requestedSearchParams);
    }

    return {
      path: resolvedPath,
      searchParams,
      memory: nextMemory,
    };
  };
}
